import { useState, useRef, useEffect } from 'react';
import { useLoanApplication } from '../../../contexts/LoanApplicationContext';

interface UploadedFile {
  id: string;
  file: File;
  preview?: string;
  status: 'uploading' | 'uploaded' | 'error';
  progress: number;
}

interface DocumentRequirement {
  id: string;
  label: string;
  required: boolean;
  description: string;
  acceptedFormats: string;
}

export default function Step3DocumentUpload() {
  const { applicationData, updateDocuments, setCurrentStep } = useLoanApplication();
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, UploadedFile>>({});
  const [dragActive, setDragActive] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Define document requirements based on loan type
  const getDocumentRequirements = (): DocumentRequirement[] => {
    const baseDocuments: DocumentRequirement[] = [
      {
        id: 'national_id_front',
        label: 'National ID (Front)',
        required: true,
        description: 'Clear photo of the front of your National ID',
        acceptedFormats: 'PDF, JPG, PNG (max 5MB)'
      },
      {
        id: 'national_id_back',
        label: 'National ID (Back)',
        required: true,
        description: 'Clear photo of the back of your National ID',
        acceptedFormats: 'PDF, JPG, PNG (max 5MB)'
      },
      {
        id: 'proof_of_residence',
        label: 'Proof of Residence',
        required: true,
        description: 'Utility bill, lease agreement, or council letter (not older than 3 months)',
        acceptedFormats: 'PDF, JPG, PNG (max 5MB)'
      },
      {
        id: 'bank_statements',
        label: '3 Months Bank Statements',
        required: true,
        description: 'Recent bank statements for the last 3 months',
        acceptedFormats: 'PDF, JPG, PNG (max 5MB)'
      }
    ];

    // Add conditional documents based on loan type
    const conditionalDocuments: DocumentRequirement[] = [];

    if (applicationData.loanDetails.loanType === 'salary-based') {
      conditionalDocuments.push({
        id: 'payslip',
        label: 'Recent Payslip',
        required: true,
        description: 'Latest payslip or salary advice from employer',
        acceptedFormats: 'PDF, JPG, PNG (max 5MB)'
      });
    }

    if (applicationData.loanDetails.loanType === 'sme-working-capital') {
      conditionalDocuments.push({
        id: 'business_registration',
        label: 'Business Registration Certificate',
        required: true,
        description: 'Company registration or business license',
        acceptedFormats: 'PDF, JPG, PNG (max 5MB)'
      });
    }

    if (applicationData.loanDetails.loanType === 'asset-backed') {
      conditionalDocuments.push({
        id: 'asset_ownership',
        label: 'Asset Ownership Proof',
        required: true,
        description: 'Title deed, vehicle registration, or asset purchase receipt',
        acceptedFormats: 'PDF, JPG, PNG (max 5MB)'
      });
    }

    return [...baseDocuments, ...conditionalDocuments];
  };

  const documentRequirements = getDocumentRequirements();

  // Load saved files from context
  useEffect(() => {
    if (applicationData.documents) {
      setUploadedFiles(applicationData.documents);
    }
  }, []);

  const validateFile = (file: File): string | null => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Please upload PDF, JPG, or PNG files only.';
    }

    if (file.size > maxSize) {
      return 'File size exceeds 5MB limit. Please upload a smaller file.';
    }

    return null;
  };

  const simulateUpload = (fileId: string, file: File) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadedFiles(prev => ({
        ...prev,
        [fileId]: {
          ...prev[fileId],
          progress,
          status: progress === 100 ? 'uploaded' : 'uploading'
        }
      }));

      if (progress >= 100) {
        clearInterval(interval);
      }
    }, 100);
  };

  const handleFileSelect = (docId: string, file: File) => {
    const error = validateFile(file);
    
    if (error) {
      setErrors(prev => ({ ...prev, [docId]: error }));
      return;
    }

    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[docId];
      return newErrors;
    });

    const fileId = `${docId}_${Date.now()}`;
    const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;

    const uploadedFile: UploadedFile = {
      id: fileId,
      file,
      preview,
      status: 'uploading',
      progress: 0
    };

    setUploadedFiles(prev => ({
      ...prev,
      [docId]: uploadedFile
    }));

    simulateUpload(docId, file);
  };

  const handleDrag = (e: React.DragEvent, docId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(docId);
    } else if (e.type === 'dragleave') {
      setDragActive(null);
    }
  };

  const handleDrop = (e: React.DragEvent, docId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(docId, e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>, docId: string) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(docId, e.target.files[0]);
    }
  };

  const removeFile = (docId: string) => {
    setUploadedFiles(prev => {
      const newFiles = { ...prev };
      if (newFiles[docId]?.preview) {
        URL.revokeObjectURL(newFiles[docId].preview!);
      }
      delete newFiles[docId];
      return newFiles;
    });
  };

  const handleNext = () => {
    const newErrors: Record<string, string> = {};
    
    documentRequirements.forEach(doc => {
      if (doc.required && !uploadedFiles[doc.id]) {
        newErrors[doc.id] = 'This document is required';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    updateDocuments(uploadedFiles);
    setCurrentStep(4);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getUploadedCount = () => {
    return Object.keys(uploadedFiles).filter(key => uploadedFiles[key]?.status === 'uploaded').length;
  };

  const getTotalRequired = () => {
    return documentRequirements.filter(doc => doc.required).length;
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Document Upload</h2>
        <p className="text-gray-600">
          Please upload the required documents for verification. All documents must be clear and legible.
        </p>
      </div>

      {/* Upload Progress Summary */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <i className="ri-shield-check-line text-teal-600 text-xl"></i>
            <span className="font-semibold text-gray-900">Secure Upload</span>
          </div>
          <span className="text-sm font-medium text-teal-700">
            {getUploadedCount()} of {getTotalRequired()} documents uploaded
          </span>
        </div>
        <p className="text-sm text-gray-600">
          All documents are encrypted and stored securely in compliance with RBZ data protection standards.
        </p>
      </div>

      {/* Document Upload Cards */}
      <div className="space-y-4">
        {documentRequirements.map((doc) => {
          const uploadedFile = uploadedFiles[doc.id];
          const hasError = errors[doc.id];

          return (
            <div
              key={doc.id}
              className={`border-2 rounded-lg p-6 transition-all ${
                dragActive === doc.id
                  ? 'border-teal-500 bg-teal-50'
                  : hasError
                  ? 'border-red-300 bg-red-50'
                  : uploadedFile?.status === 'uploaded'
                  ? 'border-teal-300 bg-teal-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{doc.label}</h3>
                    {doc.required && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                        Required
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{doc.description}</p>
                  <p className="text-xs text-gray-500">{doc.acceptedFormats}</p>
                </div>

                {uploadedFile?.status === 'uploaded' && (
                  <div className="flex items-center gap-2 text-teal-600">
                    <i className="ri-checkbox-circle-fill text-xl"></i>
                    <span className="text-sm font-medium whitespace-nowrap">Uploaded</span>
                  </div>
                )}
              </div>

              {!uploadedFile ? (
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    dragActive === doc.id
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-300 hover:border-teal-400 hover:bg-gray-50'
                  }`}
                  onDragEnter={(e) => handleDrag(e, doc.id)}
                  onDragLeave={(e) => handleDrag(e, doc.id)}
                  onDragOver={(e) => handleDrag(e, doc.id)}
                  onDrop={(e) => handleDrop(e, doc.id)}
                  onClick={() => fileInputRefs.current[doc.id]?.click()}
                >
                  <i className="ri-upload-cloud-2-line text-4xl text-gray-400 mb-3"></i>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    Drag and drop your file here, or click to browse
                  </p>
                  <p className="text-xs text-gray-500">PDF, JPG, PNG up to 5MB</p>
                  <input
                    ref={(el) => (fileInputRefs.current[doc.id] = el)}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileInputChange(e, doc.id)}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    {/* File Preview */}
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {uploadedFile.preview ? (
                        <img
                          src={uploadedFile.preview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <i className="ri-file-pdf-line text-3xl text-red-500"></i>
                      )}
                    </div>

                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {uploadedFile.file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>

                      {/* Progress Bar */}
                      {uploadedFile.status === 'uploading' && (
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-teal-600 h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${uploadedFile.progress}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Uploading... {uploadedFile.progress}%
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Remove Button */}
                    {uploadedFile.status === 'uploaded' && (
                      <button
                        onClick={() => removeFile(doc.id)}
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        type="button"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {hasError && (
                <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {hasError}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Important Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
        <div className="flex gap-3">
          <i className="ri-information-line text-amber-600 text-xl flex-shrink-0"></i>
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Important Notice</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Ensure all documents are clear, legible, and not expired</li>
              <li>• Documents must be in your legal name matching your National ID</li>
              <li>• Bank statements must show recent transactions and account balance</li>
              <li>• Incomplete or unclear documents may delay your application</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={handleBack}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
          type="button"
        >
          <i className="ri-arrow-left-line mr-2"></i>
          Back
        </button>

        <button
          onClick={handleNext}
          className="px-8 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
          type="button"
        >
          Continue to Review
          <i className="ri-arrow-right-line ml-2"></i>
        </button>
      </div>
    </div>
  );
}
